import { Skeleton } from "../../../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { MoreVerticalIcon } from "lucide-react";
import { getRtrTag, useCloneSurveyMutation, useLazyGetSurveyShareLinkQuery, useUpdateSurveyMutation } from "../../../redux/survey/survey.endpoints";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef } from "react";
import { showNotification } from "../../../redux/notification/notification.slice";
import {
  Dialog,
  DialogContent,
  DialogDescription, DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { useDashboardSurveyList } from "./useDashboardSurveyList";
import { Button } from "../../../components/ui/button";
import { Survey, SurveyStatus } from "../../../redux/survey/survey.types";
import { generateStandaloneSurveyLink } from "src/services/surveys/surveys.helpers";
import useShareRTR from "src/hooks/useShareRTR";
import { useNavigate } from "react-router-dom";
import { RadioButtonGrid, RadioButtonGridType } from "src/components/radio-button/radio-button";
import { Client } from "src/pages/client-management/types/client.types";
import { useGetOrganizationClientsQuery, useLazyGetClientSurveyAccessQuery } from "src/pages/client-management/endpoints/client.endpoints";
import { isSurveyAdmin } from "src/services/permission/permission.helpers";

type SurveyCardMenuProps = {
  readonly survey?: Survey;
  onSurveyAction: (survey: Survey, action: 'assign-client') => void;
};
const SurveyCardMenu = ({ survey, onSurveyAction }: SurveyCardMenuProps) => {
  const [getShareLink, { data: lazyLink, isFetching: isLinkFetching }] = useLazyGetSurveyShareLinkQuery();
  const [cloneSurvey, {data: clonedSurvey, isSuccess}] = useCloneSurveyMutation();
  const [updateSurvey, { isLoading: isMoveToDraftLoading }] = useUpdateSurveyMutation();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showShareWithClientDialog, setShowShareWithClientDialog] = React.useState(false);
  const [clientList, setClientList] = React.useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = React.useState<Client[]>([]);
  const [makeLive, setMakeLive] = React.useState(false);
  const [showRtrCopyDialog, setShowRtrCopyDialog] = React.useState(false);
  const [showMoveToDraftDialog, setShowMoveToDraftDialog] = React.useState(false);
  const [rtrSize, setRtrSize] = React.useState<'300x250' | '970x250'>('300x250');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const shouldCopyLink = useRef(false);
  const { onSurveyDeletion, refetch: refetchSurveyList } = useDashboardSurveyList();
  const [searchQuery, setSearchQuery] = React.useState('');
  const isClientUser = useSelector((state: any) => state.userState.isClientUser);
  const userPermissions = useSelector((state: { userState: { permissions?: { groups?: string[] } } }) => state.userState.permissions);
  const canMoveLiveSurveyToDraft = isSurveyAdmin(userPermissions) && survey?.surveyStatus === SurveyStatus.Live;

  const { onRTRShare } = useShareRTR({ surveyId: survey?.extSurveyId ?? "" });

  const { data: allClients } = useGetOrganizationClientsQuery();
  const [getClientSurveyAccess, { data: clientSurveyAccess }] = useLazyGetClientSurveyAccessQuery();

  useEffect(() => {
    if (showShareWithClientDialog && survey?.extSurveyId) {
      void getClientSurveyAccess({ ext_survey_id: survey.extSurveyId });
    }
  }, [showShareWithClientDialog, survey?.extSurveyId, getClientSurveyAccess]);

  useEffect(() => {
    if (clientSurveyAccess && allClients) {
      setSelectedClients(clientSurveyAccess.map((access) => allClients?.find((client) => client.ext_client_id === access.client_id)).filter((client) => client !== undefined) as Client[]);
    }
  }, [clientSurveyAccess, allClients]);

  useEffect(() => {
    if (allClients) {
      if (searchQuery) {
        setClientList(allClients.filter((client) =>
          client.title.toLowerCase().includes(searchQuery.toLowerCase())
        ));
      } else {
        setClientList(allClients);
      }
    }
  }, [allClients, searchQuery]);

  useEffect(() => {
    if (!lazyLink || isLinkFetching || !shouldCopyLink.current) {
      return;
    }

    void copyToClipboard(lazyLink);
  }, [lazyLink, isLinkFetching]);

  useEffect(() => {
    if (isSuccess && clonedSurvey?.extSurveyId) {
      navigate(`/surveys/create?ext-survey-id=${clonedSurvey.extSurveyId}`);

    }
  }, [isSuccess, clonedSurvey, navigate]);

  if (!survey) {
    return (
      <Skeleton className="size-6 rounded-full bg-white/50" />
    );
  }

  const copyToClipboard = async(text: string) => {
    shouldCopyLink.current = false;
    await navigator.clipboard.writeText(text);
    dispatch(showNotification({ summary: 'Successfully copied to clipboard', theme: 'success' }));
  };


  const onWebSurveyLink = () => {
    copyToClipboard(generateStandaloneSurveyLink(survey.extSurveyId));
  };

  const onRTRCopy = async() => {
    setShowRtrCopyDialog(false);

    try {
      if (makeLive) {
        await updateSurvey({
          title: survey.title,
          survey_status: 'live',
          ext_survey_id: survey.extSurveyId,
        });
      }

      const rtrTag = await getRtrTag(survey.extSurveyId, false, rtrSize);
      if (!rtrTag || rtrTag.includes('"item": null')) {
        throw new Error('Error fetching RTR tag');
      }
      copyToClipboard(rtrTag);
    } catch (error) {
      dispatch(showNotification({ summary: 'Error fetching RTR tag', theme: 'warning' }));
    }
  };

  const onShareWithClient = async() => {
    onSurveyAction(survey, 'assign-client');
  };

  const onClone = () => {
    cloneSurvey({extSurveyId: survey.extSurveyId});
  };

  const onDialogDeleteSelected = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowDeleteDialog(false);
    onSurveyDeletion(survey.extSurveyId);
  };

  const isSelectedClient = (client: Client) => {
    return selectedClients.some((selectedClient) => selectedClient.ext_client_id === client.ext_client_id);
  };

  const onConfirmMoveToDraft = async() => {
    try {
      await updateSurvey({
        title: survey.title,
        survey_status: 'draft',
        ext_survey_id: survey.extSurveyId,
      }).unwrap();
      dispatch(showNotification({ summary: 'Survey moved to draft', theme: 'success' }));
      setShowMoveToDraftDialog(false);
      void refetchSurveyList();
    } catch {
      dispatch(showNotification({ summary: 'Could not move survey to draft', theme: 'warning' }));
    }
  };

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex justify-center items-center size-6 rounded-full bg-white/50 hover:bg-white focus:transparent">
              <MoreVerticalIcon className="size-4 fill-gray-600" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="bottom"
            align="end"
            onClick={(e) => e.stopPropagation()}
          >

            <DropdownMenuItem
              className="cursor-pointer hover:bg-gray-200"
              onSelect={onWebSurveyLink}
            >
              Copy web survey link
            </DropdownMenuItem>
            <DialogTrigger asChild>
              <DropdownMenuItem
                onSelect={() => {
                  setShowRtrCopyDialog(true);
                }}
                className="cursor-pointer"
              >
                Copy RTR Tag code
              </DropdownMenuItem>
            </DialogTrigger>
            <DropdownMenuItem
              className="cursor-pointer hover:bg-gray-200"
              onSelect={onRTRShare}
            >
              Copy RTR preview link
            </DropdownMenuItem>

            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={onClone}
            >
              Clone
            </DropdownMenuItem>
            {canMoveLiveSurveyToDraft && (
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={() => setShowMoveToDraftDialog(true)}
              >
                Move to draft
              </DropdownMenuItem>
            )}
            {!isClientUser && (
              <DialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={onShareWithClient}
                  className="cursor-pointer"
                >
                  Share with Client
                </DropdownMenuItem>
              </DialogTrigger>
            )}
            <DialogTrigger asChild>
              <DropdownMenuItem
                onSelect={() => setShowDeleteDialog(true)}
                className="cursor-pointer"
              >
                Delete
              </DropdownMenuItem>
            </DialogTrigger>
          </DropdownMenuContent>
        </DropdownMenu>

        {showDeleteDialog && (
          <DialogContent
            onClick={e => e.stopPropagation()}
            className="bg-white"
          >
            <DialogHeader>
              <DialogTitle>
                Delete survey
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this survey? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="submit"
                onClick={onDialogDeleteSelected}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        )}

        {showRtrCopyDialog && (
          <DialogContent
            onClick={e => e.stopPropagation()}
            className="bg-white"
          >
            <DialogHeader>
              <DialogTitle>
                Generate RTR
              </DialogTitle>
              <small>
                Select the size of the RTR to generate
              </small>
            </DialogHeader>
            <RadioButtonGrid
              buttons={[{ label: '300x250', value: '300x250' }, { label: '970x250', value: '970x250' }]}
              extSurveyId={survey?.extSurveyId}
              initialValue={rtrSize}
              onClick={() => setRtrSize(rtrSize === '300x250' ? '970x250' : '300x250')}
              title="Dimensions"
              type={RadioButtonGridType.RtrSize}
            />
            {survey?.surveyStatus === 'draft' ? (
              <div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end">
                  <small>
                    Survey is in draft. To recieve answers, set to live.
                  </small>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                  <Button
                    variant="outline"
                    className="hover:bg-green-400"
                    type="submit"
                    onClick={() => {setMakeLive(true); onRTRCopy();}}
                  >
                    Live
                  </Button>
                  <Button
                    className="bg-gray-600 hover:bg-red-500"
                    type="submit"
                    onClick={() => {setMakeLive(false); onRTRCopy();}}
                  >
                    Draft
                  </Button>

                </div>


              </div>
            ) : (
              <DialogFooter>
                <Button
                  className="bg-gray-800 hover:bg-gray-900"
                  type="submit"
                  onClick={() => onRTRCopy()}
                >
                  Submit
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        )}
      </Dialog>
      <Dialog
        open={showMoveToDraftDialog}
        onOpenChange={setShowMoveToDraftDialog}
      >
        <DialogContent
          onClick={(e) => e.stopPropagation()}
          className="bg-white"
        >
          <DialogHeader>
            <DialogTitle>
              Move survey to draft?
            </DialogTitle>
            <DialogDescription>
              This survey is live. Moving it to draft stops new responses until you publish it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowMoveToDraftDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void onConfirmMoveToDraft()}
              disabled={isMoveToDraftLoading}
            >
              Move to draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SurveyCardMenu;
